import '../intrinsic.d.ts';
import { t } from '@trimble/i18n';
import {
  defineDropdownSelect,
  PanelLayoutWithHeaderToggle,
  SortDirection,
  SortDropDownOption,
  SortDropDownSelect,
} from '@trimble/ui-components/solidjs';
import { OptionalTierLock } from '@trimble/tiering';
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  JSX,
  onCleanup,
  on,
  onMount,
  Show,
} from 'solid-js';
import {
  FilterOption,
  PROJECT_DATA_FILTER_OPTIONS,
  ProjectDataFilterMode,
} from '../project-data.types';
import {
  BtnClasses,
  CustomTooltip,
  defineSolidCustomInput,
  IconButton,
} from '@tcweb/connect-ui';
import { debounce } from '@solid-primitives/scheduled';
import { ProjectDataPanelContext, SortByKey } from './ProjectDataPanelContext';
import {
  createProjectDataSelection,
  type ProjectDataSelection,
} from './project-data-panel-selection-factory';
import {
  useProjectDataSelectionModifiers,
  type ProjectDataSelectionModifiers,
} from './useProjectDataSelectionModifiers';
import { TieringBehaviors, TieringFeatures } from '@trimble/types';
import type { ProjectDataRepository } from '@trimble/project-data-repository';
import {
  getProjectDataSectionDefinition,
  ProjectDataPanelConfig,
  ProjectDataPanelOperations,
  ProjectDataPanelSectionData,
  ProjectDataSectionConfig,
  ProjectDataSectionDataMap,
  ProjectDataSectionKind,
  ProjectDataSectionOperationsMap,
} from './project-data-section-registry';

const DEBOUNCE_MS = 200;

interface CommonProjectDataPanelProps {
  panelTitle?: string;
  disableAddDataButton?: boolean;
  addDataTooltip?: string;
  featureTieringMap?: FeatureTieringMap;
  onAddDataClick?(): void;
  initialFilterMode?: ProjectDataFilterMode;
  onFilterModeChange?(mode: ProjectDataFilterMode): void;
  filterOptions?: FilterOption<ProjectDataFilterMode>[];
  /**
   * Called once on mount with the panel-wide `clearSelection` function, so a
   * host (e.g. an e2e test harness) can clear tree selection imperatively
   * without reaching into panel internals.
   */
  onClearSelectionRef?(clearSelection: () => void): void;
  /**
   * Host-owned selection. When omitted the panel creates its own. Pass the
   * matching {@link selectionModifiers} so mouse-down still syncs modifier state.
   */
  selection?: ProjectDataSelection;
  selectionModifiers?: ProjectDataSelectionModifiers;
}

export interface ProjectDataPanelProps extends CommonProjectDataPanelProps {
  children?: JSX.Element;
  config?: ProjectDataPanelConfig;
  repository?: ProjectDataRepository;
  operations?: ProjectDataPanelOperations;
  sectionData?: ProjectDataPanelSectionData;
}

export type FeatureTieringMap = Record<'add', TieringFeatures | undefined>;

export const ProjectDataPanel = (props: ProjectDataPanelProps): JSX.Element => {
  const [filterText, setFilterText] = createSignal<string>('');
  const [debouncedFilterText, setDebouncedFilterText] =
    createSignal<string>('');
  const [sortBy, setSortBy] = createSignal<SortByKey>('label');
  const [sortDirection, setSortDirection] = createSignal<SortDirection>('asc');
  const [showSearch, setShowSearch] = createSignal<boolean>(false);
  const [panelScrollElement, setPanelScrollElement] =
    createSignal<HTMLDivElement>();
  const configuredFilterOptions = ():
    | FilterOption<ProjectDataFilterMode>[]
    | undefined => {
    if (!props.config?.filterOptions) return undefined;
    const configuredModes = new Set(props.config.filterOptions);
    return PROJECT_DATA_FILTER_OPTIONS().filter((option) =>
      configuredModes.has(option.key)
    );
  };
  const filterModeOptions = (): FilterOption<ProjectDataFilterMode>[] => {
    return (
      props.filterOptions ??
      configuredFilterOptions() ??
      PROJECT_DATA_FILTER_OPTIONS()
    );
  };

  // Consumers may pass a subset of filter options; clamp initialFilterMode so the
  // dropdown always has a matching value (e.g. archived excluded via filterOptions).
  const resolveInitialFilterMode = (): ProjectDataFilterMode => {
    const optionKeys = filterModeOptions().map((option) => option.key);
    const requested =
      props.initialFilterMode ?? props.config?.initialFilterMode ?? 'all';
    return optionKeys.includes(requested)
      ? requested
      : (optionKeys[0] ?? 'all');
  };

  const [filterMode, setFilterMode] = createSignal<ProjectDataFilterMode>(
    resolveInitialFilterMode()
  );

  const setFilterTextDebounced = debounce(setDebouncedFilterText, DEBOUNCE_MS);
  onCleanup(() => setFilterTextDebounced.clear());
  createEffect(() => setFilterTextDebounced(filterText()));

  createEffect(
    on(
      filterMode,
      (mode) => {
        props.onFilterModeChange?.(mode);
      },
      { defer: true }
    )
  );

  const sortOptions: SortDropDownOption<SortByKey>[] = [
    {
      sortBy: 'label',
      text: t('mobileMapping.projectData.sortOptions.name'),
    },
  ];
  const handleSortChanged = (
    newSortBy: SortByKey,
    newSortDirection: SortDirection
  ): void => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
  };

  function clearAndCloseSearch(): void {
    setFilterTextDebounced.clear();
    setFilterText('');
    setDebouncedFilterText('');
    setShowSearch(false);
  }

  const ownedModifiers = useProjectDataSelectionModifiers();
  const modifiers = (): ProjectDataSelectionModifiers =>
    props.selectionModifiers ?? ownedModifiers;
  const ownedSelection = createProjectDataSelection(ownedModifiers);
  const panelSelection = createMemo(
    (): ProjectDataSelection => props.selection ?? ownedSelection
  );

  const handleBackgroundClick = (e: MouseEvent): void => {
    // Only fire for clicks on the background itself, not on a tree row that
    // bubbled up through it.
    if (e.target !== e.currentTarget) return;
    panelSelection().clearSelection();
  };

  onMount(() => {
    if (!customElements.get('tc-custom-input')) {
      defineSolidCustomInput(true);
    }
    if (!customElements.get('trb-dropdown-select')) {
      defineDropdownSelect();
    }
    props.onClearSelectionRef?.(panelSelection().clearSelection);
  });

  const renderConfiguredSection = <TKind extends ProjectDataSectionKind>(
    section: ProjectDataSectionConfig<TKind>
  ): JSX.Element => {
    const definition = getProjectDataSectionDefinition(section.kind);
    if (!definition) {
      throw new Error(
        `Project data section "${section.kind}" is not registered. Import its section package before rendering the panel.`
      );
    }

    const operations = props.operations?.[section.kind] as
      | ProjectDataSectionOperationsMap[TKind]
      | undefined;
    if (!operations) {
      throw new Error(
        `Project data section "${section.kind}" has no operations configured.`
      );
    }
    if (!props.repository) {
      throw new Error(
        `Project data section "${section.kind}" requires a repository.`
      );
    }
    const initialData = props.sectionData?.[section.kind] as
      | ProjectDataSectionDataMap[TKind]
      | undefined;
    if (definition.requiresData && initialData === undefined) {
      throw new Error(
        `Project data section "${section.kind}" has no data configured.`
      );
    }

    const content = definition.render({
      repository: props.repository,
      config: section,
      data: () =>
        props.sectionData?.[section.kind] as ProjectDataSectionDataMap[TKind],
      operations,
    });
    if (!section.tiering) return content;

    return (
      <OptionalTierLock
        preserveWidth={true}
        options={{
          disabledBehavior: TieringBehaviors.Restricted,
          showBadge: true,
          featureName: section.tiering,
        }}
      >
        {content}
      </OptionalTierLock>
    );
  };

  const PanelContent = (): JSX.Element => {
    if (!props.config) return props.children;
    if (props.children !== undefined) {
      throw new Error(
        'ProjectDataPanel accepts either config or children, but not both.'
      );
    }

    const sectionKinds = new Set<ProjectDataSectionKind>();
    for (const section of props.config.sections) {
      if (sectionKinds.has(section.kind)) {
        throw new Error(
          `Project data section "${section.kind}" is configured more than once.`
        );
      }
      sectionKinds.add(section.kind);
    }

    const orderedSections = props.config.sections
      .map((section, index) => ({ section, index }))
      .sort(
        (left, right) =>
          (left.section.order ?? Number.MAX_SAFE_INTEGER) -
            (right.section.order ?? Number.MAX_SAFE_INTEGER) ||
          left.index - right.index
      )
      .map(({ section }) => section);

    return (
      <For each={orderedSections}>
        {(section) => renderConfiguredSection(section)}
      </For>
    );
  };

  return (
    <ProjectDataPanelContext.Provider
      value={{
        filterText: debouncedFilterText,
        sortBy,
        sortDirection,
        filterMode,
        panelScrollElement,
        selection: panelSelection,
      }}
    >
      <div
        class="w-full h-full bg-base-page text-base-content"
        data-testid="project-data-panel"
      >
        <PanelLayoutWithHeaderToggle
          panelTitle={
            props.panelTitle ??
            props.config?.panelTitle ??
            t('mobileMapping.projectData.projectData')
          }
          titleSectionRight={
            <div data-testid="filter-mode-dropdown">
              <trb-dropdown-select
                prop:options={filterModeOptions()}
                prop:value={filterMode()}
                prop:onValueChange={(value) =>
                  value && setFilterMode(value as ProjectDataFilterMode)
                }
                prop:position="left"
                prop:showOnScroll={true}
              />
            </div>
          }
          rightActions={
            <>
              <Show when={props.onAddDataClick}>
                {(onAddDataClickAccessor) => (
                  <OptionalTierLock
                    preserveWidth={true}
                    options={
                      props.featureTieringMap?.add
                        ? {
                            disabledBehavior: TieringBehaviors.Restricted,
                            showBadge: true,
                            featureName: props.featureTieringMap.add!,
                          }
                        : undefined
                    }
                  >
                    <CustomTooltip
                      text={props.addDataTooltip}
                      position="right"
                      disableToolTip={!props.addDataTooltip}
                    >
                      <IconButton
                        disabled={props.disableAddDataButton}
                        color={BtnClasses.TERTIARY}
                        icon="tc-icon-content-add"
                        size="icon-medium"
                        clicked={onAddDataClickAccessor()}
                      />
                    </CustomTooltip>
                  </OptionalTierLock>
                )}
              </Show>
              <SortDropDownSelect
                options={sortOptions}
                sortBy={sortBy()}
                sortDirection={sortDirection()}
                onSortChanged={handleSortChanged}
              />
              <IconButton
                color={BtnClasses.TERTIARY}
                icon="tc-icon-search"
                size="icon-medium"
                clicked={() => setShowSearch(true)}
              />
            </>
          }
          toggleContent={
            <div class="flex items-center justify-between">
              <i class="icon-font tc-icon-search" />
              <tc-custom-input
                name="project-data-search"
                label=""
                class="w-full px-[0.5rem]"
                placeholder={t('general.search')}
                prop:onInputRef={(ref: HTMLInputElement | undefined) => {
                  ref?.focus();
                }}
                prop:onInput={(
                  e: InputEvent & {
                    currentTarget: HTMLInputElement;
                    target: Element;
                  }
                ) => setFilterText(e.currentTarget.value)}
              />
              <IconButton
                color={BtnClasses.TERTIARY}
                icon="tc-icon-content-close"
                size="icon-medium"
                clicked={clearAndCloseSearch}
              />
            </div>
          }
          showToggleContent={showSearch()}
          contentRef={setPanelScrollElement}
          content={
            <div
              class="flex flex-col min-h-full"
              data-testid="project-data-panel-background"
              onMouseDown={modifiers().syncModifier}
              onClick={handleBackgroundClick}
            >
              <PanelContent />
            </div>
          }
        />
      </div>
    </ProjectDataPanelContext.Provider>
  );
};
